import {
  kisiQueryKeys,
  useFacilityCreate,
  useFacilityEdit,
  useFacilityUpdatePicture,
  useMutationKisiUpdate,
  useQueryKisiGroupMappings,
} from "@gymflow/api";
import { cn } from "@gymflow/helpers";
import { FacilityCategoryDTO } from "@gymflow/types";
import { infiniteQueryOptions } from "@tanstack/react-query";
import { FormikProvider, useFormik } from "formik";
import { noPagination } from "libs/helpers/src/lib/pagination/noPagination";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { toFormikValidate, toFormikValidationSchema } from "zod-formik-adapter";

import { useClubSettings } from "../../../providers";
import useGymflowModels from "../../../store";
import {
  Button,
  FormInput,
  FormSelectInput,
  FormTextarea,
  Spinner,
} from "../../atoms";
import { FormImageInput } from "../../atoms/form/FormImageInput";
import { FormSelectFacilityCategory } from "../../atoms/form/selects/components/FormSelectFacilityCategory";

export const FacilityZodSchema = z.object({
  name: z.string().min(3).max(100),
  description: z.string().max(500).optional(),
  facilityCategory: z.object({
    id: z.number(),
    label: z.string(),
    value: z.custom<FacilityCategoryDTO>(),
  }),
  picture: z.string().optional(),
  kisiMapping: z
    .object({
      id: z.string(),
      label: z.string(),
    })
    .optional(),
});

export type FacilityFormProps = {
  onClose: () => void;
  className?: string;
} & (
  | {
      type: "edit";
      facilityId: number;
      initialValues: z.infer<typeof FacilityZodSchema>;
    }
  | {
      type: "create";
    }
);
export const FacilityForm = ({
  onClose,
  className,
  ...props
}: FacilityFormProps) => {
  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const { club } = useClubSettings();
  const { mutateAsync: updateFacility } = useFacilityEdit({ api });
  const { mutateAsync: createFacility } = useFacilityCreate({ api });
  const { mutateAsync: updatePicture } = useFacilityUpdatePicture({ api });
  const { mutateAsync: modifyKisiAccess } = useMutationKisiUpdate({ api });
  const formik = useFormik({
    initialValues:
      props.type === "edit"
        ? props.initialValues
        : {
            name: "",
            facilityCategory: undefined as unknown as z.infer<
              typeof FacilityZodSchema
            >["facilityCategory"],
            description: "",
            picture: "",
          },
    validationSchema: toFormikValidationSchema(FacilityZodSchema),
    validate: toFormikValidate(FacilityZodSchema),
    onSubmit: async (values) => {
      const facilityId = await (async () => {
        if (props.type === "edit") {
          await updateFacility({
            facilityId: props.facilityId,
            patchedFields: values,
          });
          return props.facilityId;
        } else {
          return (
            await createFacility({
              name: values.name,
              facilityCategoryId: values.facilityCategory.id,
              description: values.description,
            })
          ).id;
        }
      })();
      if (
        values.picture &&
        (props.type === "create" ||
          values.picture !== props.initialValues.picture)
      ) {
        await updatePicture({
          facilityId,
          file: { blob: values.picture, name: "picture.jpg" },
        });
      }
      if (
        (props.type === "create" && values.kisiMapping?.id) ||
        (props.type === "edit" &&
          values.kisiMapping?.id !== props.initialValues.kisiMapping?.id)
      ) {
        await modifyKisiAccess({
          facilityId,
          kisiGroupId: values.kisiMapping?.id ?? null,
        });
      }
      onClose();
    },
  });

  const { data: kisiData } = useQueryKisiGroupMappings({
    api,
    enabled: !!club && club.kisiStatus !== "DISABLED",
  });

  return (
    <FormikProvider value={formik}>
      <div className={cn("flex flex-col gap-4 items-center w-full", className)}>
        <div className="flex size-full flex-col gap-4 p-4">
          <FormImageInput
            name="picture"
            title={t("pages.facilitySidebar.form.imageLabel")}
            placeholder={formik.values.name
              .split(" ")
              .map((e) => e.charAt(0))
              .join("")}
            modalTitle={t("portal.common.addPicture")}
          />
          <FormInput
            name="name"
            label={t("pages.facilitySidebar.form.nameLabel")}
            placeholder={t("pages.facilitySidebar.form.nameLabel")}
          />
          <FormTextarea
            name="description"
            label={t("pages.facilitySidebar.form.descriptionLabel")}
            placeholder={t("pages.facilitySidebar.form.descriptionLabel")}
          />
          <FormSelectFacilityCategory
            name="facilityCategory"
            label={t("pages.facilitySidebar.form.facilityCategoryLabel")}
            placeholder={t("pages.facilitySidebar.form.facilityCategoryLabel")}
          />
          {club.kisiStatus !== "DISABLED" && (
            <>
              {club.kisiStatus !== "ENABLED" && (
                <div className="flex flex-row items-center gap-4 text-sm text-gray-950">
                  <Spinner className="size-8" />
                  {t("pages.facilitySidebar.form.kisiProcessing")}
                </div>
              )}
              {club.kisiStatus === "ENABLED" && (
                <FormSelectInput
                  helpTooltip={t(
                    "pages.facilitySidebar.form.kisiFacilityTooltip",
                  )}
                  dataFetchingQuery={() =>
                    infiniteQueryOptions({
                      queryKey: kisiQueryKeys.groups(),
                      queryFn: async () =>
                        (await api.kisiApi.fetchGroups()).data,
                      select: (data) => {
                        return {
                          pageParams: data.pageParams,
                          pages: data.pages.map((page) =>
                            page
                              .map((group) => {
                                const disabled = kisiData?.some(
                                  (assignment) =>
                                    assignment.kisiGroupId === group.id &&
                                    assignment.type === "MEMBERSHIP",
                                );
                                return {
                                  id: group.id,
                                  label: group.name,
                                  value: group,
                                  disabled: disabled,
                                  tooltip: disabled
                                    ? "This group is already assigned to a membership"
                                    : undefined,
                                };
                              })
                              .sort((a, b) =>
                                a.disabled
                                  ? 1
                                  : b.disabled
                                    ? -1
                                    : a.label.localeCompare(b.label),
                              ),
                          ),
                        };
                      },
                      ...noPagination,
                    })
                  }
                  name="kisiMapping"
                  label={t("pages.facilitySidebar.form.kisiFacility")}
                  placeholder={t(
                    "pages.facilitySidebar.form.kisiFacilityPlaceholder",
                  )}
                  isClearable
                />
              )}
            </>
          )}
        </div>
        <div className="flex w-full flex-row items-center justify-end gap-x-2 border-t border-gray-200 bg-gray-50 px-6 py-4">
          <Button onClick={() => onClose()} className="flex-1">
            {t("portal.common.cancel")}
          </Button>
          <Button
            intent="secondary"
            className="flex-1"
            onClick={async () => {
              await formik.submitForm();
            }}
          >
            {props.type === "edit"
              ? t("portal.common.edit")
              : t("portal.common.create")}
          </Button>
        </div>
      </div>
    </FormikProvider>
  );
};
